/**
 * WeatherFacade — read a `Weather` record for a date off a
 * weather-shaped dataSource. Replaces the deleted
 * `WeatherRow.getWeather`. Read-only from the client; provider
 * sync writes cells keyed by the row's `lookupKey`.
 *
 * Obtained via `row.as(WEATHER_FACADE)`. Returns null when the
 * row isn't an obj single-cardinality source.
 */

import type { Option } from "../core/option.ts";
import type { NaiveDate } from "@tento-chrono";

import { defineShape, type Shape } from "../core/shape.ts";
import type { Weather } from "../core/dtype.ts";
import { FacadeKey, type FacadeFactory } from "./facade.ts";
import { SourceCells } from "../rows/variants/source-cells.ts";
import type { SourceRow } from "../rows/variants/source-row.ts";
import { deepFrozen } from "../core/frozen-collections.ts";

/** The facade key that reaches a source row's weather facade. */
export const WEATHER_FACADE = new FacadeKey<WeatherFacade>("weather");

/**
 * Structural contract for a `Weather` cell — any value with a
 * numeric `temperature` + a DateTime-ish `observedAt`.
 */
export const WEATHER_SHAPE: Shape<Weather> = defineShape<Weather>(
  "weather/v1",
  (value): value is Weather => {
    if (typeof value !== "object" || value === null) return false;
    if (!("temperature" in value) || typeof value.temperature !== "number") return false;
    return "observedAt" in value && isInstant(value.observedAt);
  },
);

/** A DateTime-ish value: an object with a numeric `mse` (ms since epoch). */
function isInstant(value: unknown): boolean {
  return typeof value === "object" && value !== null && "mse" in value &&
    typeof value.mse === "number";
}

export class WeatherFacade {
  readonly #row: SourceRow;

  constructor(row: SourceRow) {
    this.#row = row;
  }

  /** Weather for a date — null if not synced yet or shape mismatch. */
  at(date: NaiveDate.Partial): Option<Weather> {
    const cell = SourceCells.dates(this.#row.peekCells(date.toRange()))[0];
    if (!cell || !WEATHER_SHAPE.probe(cell.value)) return null;
    return cell.value;
  }
}

export namespace WeatherFacade {
  export const factory: FacadeFactory<WeatherFacade> = deepFrozen({
    key: WEATHER_FACADE,
    build(row: SourceRow): Option<WeatherFacade> {
      if (row.descriptor.role !== "source") return null;
      if (row.descriptor.dtype !== "obj") return null;
      if (row.descriptor.cellCardinality !== "single") return null;
      return new WeatherFacade(row);
    },
  });
}
